import { useState, useEffect, useReducer, useCallback } from "react";
import * as React from "react";
import queryString from "query-string";
import Backend from "./components/providers/DndBackend";
import { ThemeProvider } from "@emotion/react";
import defaultTheme from "./theme";

import { DndProvider } from "react-dnd";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";
import { Router } from "@reach/router";
import appStyles from "./App.module.css";
import OrientationContext, { LANDSCAPE, PORTRAIT, type Orientation } from "./components/providers/OrientationProvider";
import BoardPropertiesContext, { initialBoardProperties } from "./components/providers/BoardPropertiesProvider";
import GameSettingsContext, { defaultGameSettings } from "./components/providers/GameSettingsProvider";
import GameStatePropertiesContext from "./components/providers/GameStateProvider";
import SelectedHoleContext from "./components/providers/SelectedHoleProvider";
import GameDispatchContext from "./components/providers/GameDispatchProvider";
import GameReducerStateContext from "./components/providers/GameReducerStateProvider";
import { gameStateReducer, initialState } from "./reducer";
import { opponent } from "./core/baoside";
import { loadShowHints, saveShowHints } from "./utils/showHintsStorage";
import type { Settings } from "./core/types";
import { AuthProvider } from "./contexts/AuthContext";
import { useAutoAuth } from "./hooks/useAutoAuth";
import { usePostHogIdentity } from "./hooks/usePostHogIdentity";
import { ToastProvider } from "./components/Toast";

import BoardContainer from "./components/Board";
import MultiplayerBoardContainer from "./components/Board/MultiplayerBoardContainer";
import Home from "./components/Home";
import Contact from "./components/Contact";
import HelpSection from "./components/HelpSection";
import ProfileRoute from "./components/Profile/ProfileRoute";
import { CookieBanner } from "./components/CookieBanner/CookieBanner";
import MultiplayerLobby from "./components/MultiplayerLobby/MultiplayerLobby";
import CreateGame from "./components/CreateGame/CreateGame";
import JoinGame from "./components/JoinGame/JoinGame";
import Drawer from "./components/Drawer";
import AppMenu from "./components/AppMenu";
import MenuButton from "./components/MenuButton";
import SettingsForm from "./components/SettingsForm";
import History from "./components/History";
import GameOverModal from "./components/GameOverModal";
import LanguageGate from "./components/LanguageGate";
import { EnabledLanguagesProvider } from "./components/providers/EnabledLanguagesProvider";

// Component that handles auto authentication and PostHog identity syncing
const AutoAuthHandler = (): null => {
  useAutoAuth();
  usePostHogIdentity();
  return null;
};

function App(): React.ReactNode {
  const { innerWidth, innerHeight } = window;
  const [orientation, setOrientation] = useState(innerWidth < innerHeight ? PORTRAIT : LANDSCAPE);
  const [theme, setTheme] = useState(defaultTheme);
  const [boardProperties, setBoardProperties] = useState(initialBoardProperties);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const query = queryString.parse(window.location.search);
  const startingSide = query.startingSide === "0" ? 0 : 1;

  // Game settings state (lifted from BoardContainer). Lazy-init the persisted
  // showHints preference from localStorage (default-on if absent/corrupt).
  const [settings, setSettingsState] = useState<Settings>(() => ({
    ...defaultGameSettings.settings,
    showHints: loadShowHints()
  }));

  // Single persistence choke-point: every settings update persists the
  // showHints preference, swallowing any storage errors.
  const setGameSettings = useCallback((updated: Settings) => {
    saveShowHints(updated.showHints);
    setSettingsState(updated);
  }, []);
  const [gameType, setGameType] = useState(defaultGameSettings.gameType);
  const [gameStage, setGameStage] = useState(defaultGameSettings.gameStage);

  // Game state reducer (lifted from BoardContainer)
  const [state, dispatch] = useReducer(gameStateReducer, {
    ...initialState,
    currentPlayer: startingSide,
    settings: {
      ...settings,
      startingSide
    }
  });

  useEffect(() => {
    const onResize = () => {
      const { innerWidth, innerHeight, location } = window;
      const isOnHelpScreen = location.pathname.includes("/help");
      // update the variables for size
      const isPortrait = innerWidth < innerHeight;
      const [width, height] = isPortrait ? [innerHeight, innerWidth] : [innerWidth, innerHeight];
      const minBoardWidth = 600;
      const boardWidth = Math.min(minBoardWidth, width - 20);
      const boardHeight = Math.min(height, boardWidth / 2);
      const holeWidth = (boardWidth - 10) / 8 - 16;
      const holeHeight = (boardHeight - 10) / 4 - 16;
      const appElement = document.body;
      if (appElement) {
        appElement.style.setProperty("--holeHeight", `${holeHeight}px`);
        appElement.style.setProperty("--holeWidth", `${holeWidth}px`);
        appElement.style.setProperty("--boardHeight", `${boardHeight}px`);
        appElement.style.setProperty("--boardWidth", `${boardWidth}px`);
        if (isOnHelpScreen && isPortrait) {
          appElement.style.setProperty("--boardHeight", `${boardWidth}px`);
          appElement.style.setProperty("--boardWidth", `${boardHeight}px`);
        }
      }
      const properties = {
        holeWidth,
        holeHeight,
        boardWidth,
        boardHeight,
        highlightedHoles: { "0": [] as number[], "1": [] as number[] }
      };
      setOrientation(isPortrait ? PORTRAIT : LANDSCAPE);
      setBoardProperties(properties);
      setTheme({
        ...defaultTheme,
        holeHeight,
        holeWidth,
        boardHeight,
        boardWidth
      });
    };
    onResize();
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return (
    <I18nextProvider i18n={i18n}>
      <EnabledLanguagesProvider>
        <AuthProvider>
          <AutoAuthHandler />
          <LanguageGate />
          <ThemeProvider theme={theme}>
            <ToastProvider>
              <DndProvider backend={Backend}>
                <OrientationContext.Provider value={orientation}>
                  <BoardPropertiesContext.Provider value={{ setBoardProperties, boardProperties }}>
                    <GameSettingsContext.Provider
                      value={{
                        settings,
                        gameStage,
                        gameType,
                        gameMode: "single",
                        setGameStage,
                        setGameType,
                        setGameSettings
                      }}
                    >
                      <GameDispatchContext.Provider value={{ dispatch }}>
                        <GameReducerStateContext.Provider value={state}>
                          <SelectedHoleContext.Provider value={state.gamePlay}>
                            <GameStatePropertiesContext.Provider
                              value={{
                                properties: {
                                  nextPlayer: opponent(state.currentPlayer),
                                  gameState: state.currentGameState,
                                  moveHistory: state.moveHistory
                                }
                              }}
                            >
                              <Router>
                                <AppContent
                                  path="/*"
                                  orientation={orientation}
                                  isMenuOpen={isMenuOpen}
                                  setIsMenuOpen={setIsMenuOpen}
                                />
                              </Router>
                            </GameStatePropertiesContext.Provider>
                          </SelectedHoleContext.Provider>
                        </GameReducerStateContext.Provider>
                      </GameDispatchContext.Provider>
                    </GameSettingsContext.Provider>
                  </BoardPropertiesContext.Provider>
                </OrientationContext.Provider>
              </DndProvider>
            </ToastProvider>
          </ThemeProvider>
        </AuthProvider>
      </EnabledLanguagesProvider>
    </I18nextProvider>
  );
}

// Separate component that contains the app structure and is a child of Router
const AppContent = ({
  orientation,
  isMenuOpen,
  setIsMenuOpen
}: {
  orientation: Orientation;
  isMenuOpen: boolean;
  path: string;
  setIsMenuOpen: (value: boolean) => void;
}): React.ReactNode => {
  return (
    <div className={appStyles.App}>
      <MenuButton onClick={() => setIsMenuOpen(true)} />
      <Drawer isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)}>
        <AppMenu onClose={() => setIsMenuOpen(false)} />
      </Drawer>
      <Router
        css={({ boardHeight, boardWidth, gridUnit }) => ({
          width: orientation === PORTRAIT ? boardHeight : boardWidth,
          height: orientation === PORTRAIT ? boardWidth : boardHeight
        })}
      >
        <Home path="/" />
        <HelpSection path="/game/help/:screenIndex" />
        <HelpSection path="/game/help" />
        <SettingsForm path="/game/settings" />
        <History path="/game/history" />
        <GameOverModal path="/game/restart" />
        <BoardContainer path="/game" />
        <Contact path="/contact" />
        <ProfileRoute path="/profile" />
        <MultiplayerLobby path="/multiplayer" />
        <CreateGame path="/multiplayer/create" />
        <JoinGame path="/multiplayer/join" />
        <MultiplayerBoardContainer path="/multiplayer/game/:gameId/*" />
      </Router>
      <CookieBanner />
    </div>
  );
};

export default App;
